import { ShieldCheck, Printer, Check, AlertTriangle, Lock } from "lucide-react";
import { Button } from "../components/ui/button";

const mono = { fontFamily: "'JetBrains Mono', monospace" };
const jakarta = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

const STEPS = [
  ["01", "Nahrajete ŠVP", "PDF nebo Word (.docx). Vše proběhne automaticky."],
  ["02", "Proběhne kontrola", "Přes 600 kontrolních bodů vůči novému RVP PV."],
  ["03", "Uvidíte zjištění", "Konkrétní nálezy s citacemi a čísly stran z vašeho dokumentu."],
];

const BENEFITS = [
  "Chybějící povinné náležitosti ŠVP",
  "Pokrytí vzdělávacích oblastí a klíčových kompetencí",
  "Odkazy na místa, která neodpovídají novému RVP PV",
  "Každé zjištění s důkazem — strana a citace z vašeho ŠVP",
];

const ResultsMock = () => (
  <div className="overflow-hidden rounded-xl border border-slate-300 bg-white shadow-lg" data-testid="flyer-results-mock">
    <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-4 py-2">
      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500" style={mono}>Výsledek plné diagnózy vašeho ŠVP</span>
      <span className="rounded bg-emerald-100 px-2 py-0.5 text-[9px] font-bold text-emerald-800" style={mono}>664 kontrol dokončeno</span>
    </div>
    <div className="grid grid-cols-4 divide-x divide-slate-100 border-b border-slate-100 text-center">
      {[["5", "kritických", "text-red-700"], ["8", "závažných", "text-amber-700"], ["14", "středních", "text-blue-700"], ["6", "k ověření", "text-slate-400"]].map(([n, l, c]) => (
        <div key={l} className="py-2">
          <div className={`text-xl font-extrabold leading-none ${c}`} style={jakarta}>{n}</div>
          <div className="mt-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-400" style={mono}>{l}</div>
        </div>
      ))}
    </div>
    <div className="grid grid-cols-[55fr_45fr] divide-x divide-slate-100">
      <div className="p-4">
        <div className="flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-red-700" style={mono}>Kritické zjištění · Pedagogické diagnostikování</span>
        </div>
        <div className="mt-2 text-[9px] font-bold uppercase tracking-widest text-slate-400" style={mono}>Vaše ŠVP · strana 22</div>
        <p className="mt-1 rounded-lg bg-slate-50 p-2.5 text-[12px] italic leading-relaxed text-slate-600">
          „…sledujeme <mark className="rounded bg-yellow-200 px-0.5">pokroky dětí průběžně při běžných činnostech</mark>, poznatky si předáváme ústně na poradách…“
        </p>
        <p className="mt-2 text-[12px] leading-snug text-slate-700">Sledování dítěte není popsáno jako ucelený systém, jak vyžaduje nový RVP PV.</p>
        <div className="mt-2 flex flex-wrap gap-1.5 text-[9px]" style={mono}>
          <span className="rounded bg-slate-900 px-1.5 py-0.5 font-bold text-white">POVINNÉ</span>
          <span className="rounded bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-800">splněno jen částečně</span>
          <span className="rounded bg-red-100 px-1.5 py-0.5 font-semibold text-red-800">závažnost 9/10</span>
        </div>
      </div>
      <div className="p-4">
        <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400" style={mono}>Pokrytí vzdělávacích oblastí</div>
        <div className="mt-2 space-y-1.5">
          {[["Dítě a jeho tělo", 92, "bg-emerald-500"], ["Dítě a jeho psychika", 78, "bg-emerald-500"], ["Dítě, ten druhý a společnost", 64, "bg-amber-500"], ["Dítě a svět", 41, "bg-red-500"]].map(([n, v, c]) => (
            <div key={n}>
              <div className="flex justify-between text-[10px] text-slate-600"><span>{n}</span><span className="font-bold" style={mono}>{v} %</span></div>
              <div className="mt-0.5 h-1 rounded-full bg-slate-100"><div className={`h-1 rounded-full ${c}`} style={{ width: `${v}%` }} /></div>
            </div>
          ))}
        </div>
        <div className="mt-2.5 space-y-1 text-[10px] text-slate-500">
          {[["Nadané děti — systém podpory", "str. 17"], ["Jazyková podpora dětí s OMJ", "nedoloženo"]].map(([t, p]) => (
            <div key={t} className="flex items-center justify-between rounded-md border border-slate-100 px-2 py-1">
              <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" />{t}</span>
              <span className="font-semibold text-slate-400" style={mono}>{p}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
    <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50 px-4 py-1.5 text-[10px] text-slate-500">
      <span>+ 24 dalších zjištění s citacemi z vašeho dokumentu · export PDF a Excel</span>
      <Lock className="h-3 w-3" />
    </div>
  </div>
);

export default function FlyerPage() {
  return (
    <div className="min-h-screen bg-slate-200 py-8 print:min-h-0 print:bg-white print:py-0">
      <div className="mx-auto mb-5 flex max-w-[210mm] items-center justify-between px-2 print:hidden">
        <p className="text-sm text-slate-600">Leták A4 — vytiskněte a rozdejte kolegyním či na poradě ředitelek.</p>
        <Button onClick={() => window.print()} className="h-12 bg-emerald-600 px-6 text-base font-bold hover:bg-emerald-700" data-testid="print-flyer-button">
          <Printer className="mr-2 h-5 w-5" /> Vytisknout
        </Button>
      </div>

      <div className="mx-auto flex h-[297mm] w-[210mm] flex-col bg-white p-[12mm] shadow-xl print:shadow-none" data-testid="flyer-page">
        {/* Hlavička */}
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-600">
            <ShieldCheck className="h-6 w-6 text-white" />
          </div>
          <div>
            <div className="text-xl font-extrabold tracking-tight text-slate-900" style={jakarta}>AuditŠVP</div>
            <div className="text-[11px] text-slate-500">Diagnostika ŠVP vůči požadavkům RVP PV</div>
          </div>
        </div>

        <h1 className="mt-5 text-[28px] font-extrabold leading-[1.2] tracking-tight text-slate-900" style={jakarta}>
          Je vaše nové ŠVP v souladu s RVP PV 2025?
        </h1>
        <p className="mt-1.5 text-base text-slate-600">Nahrajte své ŠVP a za pár minut víte, na čem jste.</p>

        {/* 3 kroky */}
        <div className="mt-5 grid grid-cols-3 gap-3">
          {STEPS.map(([n, t, d]) => (
            <div key={n} className="rounded-xl border-2 border-emerald-100 bg-emerald-50/50 p-3.5">
              <div className="text-2xl font-extrabold leading-none text-emerald-600" style={jakarta}>{n}</div>
              <div className="mt-1.5 text-sm font-bold text-slate-900">{t}</div>
              <div className="mt-1 text-xs leading-relaxed text-slate-600">{d}</div>
            </div>
          ))}
        </div>

        {/* Co zjistíte */}
        <div className="mt-5 grid grid-cols-2 gap-x-5 gap-y-2">
          {BENEFITS.map((b) => (
            <div key={b} className="flex items-start gap-2">
              <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-600">
                <Check className="h-2.5 w-2.5 text-white" />
              </span>
              <span className="text-[13px] leading-snug text-slate-700">{b}</span>
            </div>
          ))}
        </div>

        {/* Náhled výsledků */}
        <div className="mt-5">
          <div className="mb-2 text-center text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700" style={mono}>
            Takto podrobný výsledek dostanete
          </div>
          <ResultsMock />
        </div>

        {/* Cena */}
        <div className="my-auto flex items-center justify-between gap-5 rounded-xl bg-emerald-600 px-5 py-3 text-white">
          <div>
            <div className="text-base font-extrabold leading-tight">Náhled výsledků zdarma</div>
            <div className="mt-0.5 text-xs text-emerald-100">Plná diagnóza jednorázově · 5 kontrol v ceně · faktura pro školu</div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-extrabold leading-none" style={jakarta}>1 990 Kč</div>
            <div className="mt-0.5 text-[10px] text-emerald-100">jednorázově</div>
          </div>
        </div>

        {/* Web + patička */}
        <div>
          <div className="rounded-xl border-2 border-slate-200 py-2.5 text-center">
            <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Vyzkoušejte na</span>
            <span className="ml-3 text-2xl font-extrabold tracking-tight text-emerald-700" style={jakarta}>www.auditsvp.cz</span>
          </div>
          <p className="mt-2.5 text-center text-[9px] leading-relaxed text-slate-400">
            AuditŠVP je diagnostický podpůrný nástroj — nenahrazuje stanovisko MŠMT, NPI ani ČŠI.
            · JustRight Stage s.r.o. · IČO: 08588023 · Zámostní 1155/27, Slezská Ostrava, 710 00 Ostrava
          </p>
        </div>
      </div>
    </div>
  );
}
